import { useCallback, useEffect, useState } from 'react';
import { KpiBenchmarks, KpiBenchmarkValue } from 'benchmarking-domain/kpi-benchmark';
import { KpiCategory, KpiStructure } from 'benchmarking-domain/kpi-definition';
import { KpiStructureDependencyFinder } from 'benchmarking-domain/kpi-structure-dependency-finder';
import { KpiStatistics, KpiValues } from 'benchmarking-domain/kpi-value';
import { PlantKpiHistory } from 'benchmarking-domain/plant-kpi-history';
import Project from 'benchmarking-domain/project';
import ProjectSection from 'benchmarking-domain/project-section';
import { Unsubscribe } from 'firebase/auth';
import { debounceLeadingAsync } from 'helpers/function';
import useApi from 'hooks/use-api';
import { useOrganisation } from 'hooks/use-organisation';
import { round } from 'mathjs';
import { enqueueSnackbar } from 'notistack';

const RoundingPrecision_2 = 2;
const RoundingPrecision_4 = 4;

export function useKpiStructure(
  project: Project,
  projectSections: ProjectSection[] | undefined,
  scopeKpiSectionId?: string
) {
  const [kpiStructure, setKpiStructure] = useState<KpiStructure>();
  const [kpiBenchmarks, setKpiBenchmarks] = useState<KpiBenchmarks>();
  const [kpiStatistics, setKpiStatistics] = useState<Map<string, KpiStatistics> | undefined>();
  const [kpiHistoryItems, setKpiHistoryItems] = useState<Map<string, PlantKpiHistory> | undefined>();
  const [scopeKpiValues, setScopeKpiValues] = useState<KpiValues | undefined>();
  const [initLoading, setInitLoading] = useState<boolean>(true);

  const {
    subscribeCurrentKpiStructure,
    subscribeCurrentKpiBenchmark,
    getKpiStatisticsForPlant,
    subscribeProjectAllKpis,
    subscribeKpiForSection,
    getPlantHistoryForKpis,
  } = useApi();

  const { organisationId } = useOrganisation();

  const filterKpiStructureByTechnology = useCallback(
    (structure: KpiStructure, technology: string): KpiStructure => {
      if (!structure.categories) {
        return structure;
      }

      const newStructure = {
        ...structure,
        categories: structure.categories.map((category) => {
          return {
            ...category,
            subCategories:
              category.subCategories?.filter((subCategory) => {
                const isMatchingTechnology =
                  !subCategory.technology || subCategory.technology.toLowerCase() === technology.toLowerCase();
                return isMatchingTechnology;
              }) || [],
          };
        }),
      };

      const imposeNameVariablesOnCategory = (
        category: KpiCategory,
        variables: { [varName: string]: string }
      ): KpiCategory => {
        return {
          ...category,
          id: KpiStructureDependencyFinder.imposeNameVariables(category.id, variables),
          name: KpiStructureDependencyFinder.imposeNameVariables(category.name, variables),
          kpiDefinitions: category.kpiDefinitions.map((kpi) => {
            const keyParts = KpiStructureDependencyFinder.imposeNameVariables(kpi.id, variables).split('@');
            let id = keyParts[0];
            let dynamicKpiParentName = '';

            if (keyParts.length > 1) {
              dynamicKpiParentName = keyParts[keyParts.length - 1];
              id = keyParts.slice(0, keyParts.length - 1).join('@');
            }

            return {
              ...kpi,
              id,
              name: KpiStructureDependencyFinder.imposeNameVariables(kpi.name, variables),
              calculationPhrase: KpiStructureDependencyFinder.imposeNameVariables(kpi.calculationPhrase, variables),
              dynamicKpiParentName,
            };
          }),
          subCategories: category.subCategories?.map((subCategory) =>
            imposeNameVariablesOnCategory(subCategory, variables)
          ),
        };
      };

      const appendDynamicCategories = (category: KpiCategory, variables: { [varName: string]: string }) => {
        if (!category || !projectSections) {
          return;
        }

        if (category.subCategories) {
          category.subCategories.forEach((subCategory) => {
            appendDynamicCategories(subCategory, variables);
          });
        }

        if (!category.dynamicKpiCategories) {
          return;
        }

        for (const dynamicCategory of category.dynamicKpiCategories) {
          const dynamicCategoryDefinition = structure.dynamicKpis.find(
            (dk) => dk.questionnaireId === dynamicCategory.questionnaireId
          );
          const dynamicItems = projectSections.find(
            (section) => section.id === dynamicCategory.sectionId
          )?.dynamicItems;

          if (!dynamicItems || !dynamicCategoryDefinition) {
            continue;
          }

          dynamicItems?.forEach((di) => {
            const nameVariables = { sectionId: dynamicCategory.sectionId, dynamicItemId: di };
            dynamicCategoryDefinition.categories.forEach((dynamicSubCategoryDefinition) => {
              if (!category.subCategories) {
                category.subCategories = [];
              }
              category.subCategories.push(imposeNameVariablesOnCategory(dynamicSubCategoryDefinition, nameVariables));
            });
          });
        }
      };

      newStructure.categories?.forEach((category) => appendDynamicCategories(category, {}));

      return newStructure;
    },
    [projectSections]
  );

  useEffect(() => {
    if (!project || !organisationId) {
      return;
    }

    let unsubscribeScopeKpiValues: Unsubscribe | undefined = undefined;

    if (scopeKpiSectionId == undefined) {
      unsubscribeScopeKpiValues = subscribeProjectAllKpis(organisationId, project.id, (result) => {
        if (result?.kpiValues) {
          Object.keys(result.kpiValues).forEach((key) => {
            result.kpiValues[key] = getRoundedValue(result.kpiValues[key]);
          });
        }
        setScopeKpiValues(result?.kpiValues);
      });
    } else {
      unsubscribeScopeKpiValues = subscribeKpiForSection(organisationId, project.id, scopeKpiSectionId, (result) => {
        if (result?.kpiValues) {
          Object.keys(result.kpiValues).forEach((key) => {
            result.kpiValues[key] = getRoundedValue(result.kpiValues[key]);
          });
        }
        setScopeKpiValues(result?.kpiValues);
      });
    }

    const unsubscribeCurrentKpiStructure = subscribeCurrentKpiStructure(project.frameworkId, (result) => {
      if (result) {
        const filteredResult = filterKpiStructureByTechnology(result, project.technology);
        setKpiStructure(filteredResult);
      }
    });

    return () => {
      unsubscribeScopeKpiValues && unsubscribeScopeKpiValues();
      unsubscribeCurrentKpiStructure();
    };
  }, [
    organisationId,
    project,
    scopeKpiSectionId,
    subscribeKpiForSection,
    subscribeProjectAllKpis,
    subscribeCurrentKpiStructure,
    filterKpiStructureByTechnology,
  ]);

  const fetchKpiStatistics = useCallback(
    async (organisationId: string, project: Project, sectionId: string | undefined) => {
      try {
        const allKpiStats = await getKpiStatisticsForPlant(organisationId, project.id, sectionId);

        if (allKpiStats) {
          allKpiStats.forEach((kpiStat) => {
            Object.keys(kpiStat.facilityValues).forEach((key) => {
              kpiStat.facilityValues[key] = getRoundedValue(kpiStat.facilityValues[key]);
            });
          });
        }

        setKpiStatistics(allKpiStats);
      } catch (error) {
        enqueueSnackbar('Failed to fetch KPI statistics', { variant: 'error' });
        console.error('Error fetching KPI statistics:', error);
      }
    },
    [getKpiStatisticsForPlant]
  );

  const fetchKpiHistoryItems = useCallback(
    async (organisationId: string, project: Project, sectionId: string | undefined) => {
      try {
        const allKpiHistoryItems = await getPlantHistoryForKpis(organisationId, project.id, sectionId);

        const kpiHistoryMap = new Map(
          allKpiHistoryItems?.map((obj) => {
            if (obj.projectValues) {
              Object.keys(obj.projectValues).forEach((key) => {
                obj.projectValues[key].value = getRoundedValue(obj.projectValues[key].value);
              });
            }
            return [obj.kpiId, obj];
          })
        );

        setKpiHistoryItems(kpiHistoryMap);
      } catch (error) {
        enqueueSnackbar('Failed to fetch KPI statistics', { variant: 'error' });
        console.error('Error fetching KPI statistics:', error);
      }
    },
    [getPlantHistoryForKpis]
  );

  const getRoundedValue = (value: number | undefined): number => {
    if (value === undefined) {
      return 0;
    }

    let roundedValue = round(value, RoundingPrecision_2);

    if (roundedValue === 0 && value !== 0) {
      roundedValue = round(value, RoundingPrecision_4);
    }

    return roundedValue;
  };

  const handleKpiBenchmarkUpdate = useCallback(
    async (organisationId: string, project: Project, sectionId: string | undefined, result?: KpiBenchmarks) => {
      await Promise.all([
        fetchKpiStatistics(organisationId, project, sectionId),
        fetchKpiHistoryItems(organisationId, project, sectionId),
      ]);

      if (result) {
        Object.values(result.kpisBenchmarks).forEach((kpiBenchmarkValue: KpiBenchmarkValue) => {
          kpiBenchmarkValue.bottomQuartile = kpiBenchmarkValue.bottomQuartile
            ? getRoundedValue(kpiBenchmarkValue.bottomQuartile)
            : kpiBenchmarkValue.bottomQuartile;
          kpiBenchmarkValue.median = kpiBenchmarkValue.median
            ? getRoundedValue(kpiBenchmarkValue.median)
            : kpiBenchmarkValue.median;
          kpiBenchmarkValue.topQuartile = kpiBenchmarkValue.topQuartile
            ? getRoundedValue(kpiBenchmarkValue.topQuartile)
            : kpiBenchmarkValue.topQuartile;
        });

        setKpiBenchmarks(result);
      }
    },
    [fetchKpiStatistics, fetchKpiHistoryItems]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleKpiBenchmarkUpdateDebounce = useCallback(
    debounceLeadingAsync(async (organisationId: string, result?: KpiBenchmarks) => {
      await handleKpiBenchmarkUpdate(organisationId, project, scopeKpiSectionId, result);
      setInitLoading(false);
    }, 1000),
    [handleKpiBenchmarkUpdate]
  );

  useEffect(() => {
    if (!project || !organisationId) {
      return;
    }

    const unsubscribeCurrentKpiBenchmark = subscribeCurrentKpiBenchmark(
      project.frameworkId,
      project.technology,
      (result?: KpiBenchmarks) => handleKpiBenchmarkUpdateDebounce(organisationId, result)
    );

    return () => unsubscribeCurrentKpiBenchmark();
  }, [organisationId, project, subscribeCurrentKpiBenchmark, handleKpiBenchmarkUpdateDebounce]);

  return {
    kpiStructure,
    kpiBenchmarks,
    kpiStatistics,
    scopeKpiValues,
    kpiHistoryItems,
    initLoading,
  };
}
